/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, {
  PADDING_VERTICAL,
  type ComponentStyles,
  PADDING_HORIZONTAL_ROUND,
  BORDER_WIDTH,
  PADDING_HORIZONTAL_SQUARE,
} from './WireframeBase';
import tools from '../tools';
import wireframeButton from '../assets/icons/wireframeButton.svg';
import wireframeStateRoundHollow from '../assets/icons/wireframeStateRoundHollow.svg';
import wireframeStateSquareHollow from '../assets/icons/wireframeStateSquareHollow.svg';
import wireframeStateRoundFilled from '../assets/icons/wireframeStateRoundFilled.svg';
import wireframeStateSquareFilled from '../assets/icons/wireframeStateSquareFilled.svg';
import wireframeStateLink from '../assets/icons/wireframeStateLink.svg';
import { SPACER_1, SPACER_2, SPACER_3 } from '../utils/style';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';
import {
  FlexColumn,
  type FlexColumnProps,
} from '@lib/canvasx/components/FlexColumn';
import { WIREFRAME_PRIMARY_COLOR } from '../utils/wireframes';
import { Placeholder } from '../components/wireframes/Placeholder';

const ROUND_RADIUS = 32;
const SQUARE_RADIUS = SPACER_1;
const UNDERLINE_OFFSET = SPACER_1;
const PADDING_LINK = SPACER_2;

class WireframeButton extends WireframeBase {
  constructor(fabricObject) {
    super(fabricObject);

    const items = this.fabricObject.items;
    // Backwards compat: originally buttons had an item array of 1 for the placeholder
    // text. This moves it out of the items array and into the 'placeholder' field
    if (items.items.length > 0) {
      const placeholder = items.items[0];
      this.addPlaceholder(placeholder);
      // Clear item collection for buttons
      this.fabricObject.set(items.dataKey, null);
    }
  }

  initItems() {
    if (!this.placeholder?.value) {
      this.addPlaceholder({ value: 'Button', state: 'default' });
    }
  }

  tools() {
    // Limit user to text color option only when using a filled state. Otherwise
    // text color will match fill/border color
    const textTools =
      this.filled || this.isLink ? [tools.ToolWireframeTextColor] : [];
    return [
      [tools.ToolWireframeComponentStyle],
      [tools.ToolWireframeComponentState],
      [tools.ToolSize],
      [tools.ToolWireframeIcon],
      this.isLink ? [] : [tools.ToolFillColorWithoutOpacity],
      textTools,
    ];
  }

  static iconSvg() {
    return wireframeButton;
  }

  static externalName(): string {
    return 'Button';
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
      mt: new WireframeLinearResizeControl({ edge: 'top' }),
      mb: new WireframeLinearResizeControl({ edge: 'bottom' }),
    };
  }

  static componentStyles(): ComponentStyles {
    return {
      link: wireframeStateLink,
      round: wireframeStateRoundHollow,
      square: wireframeStateSquareHollow,
      roundFilled: wireframeStateRoundFilled,
      squareFilled: wireframeStateSquareFilled,
    };
  }

  componentStyles(): ComponentStyles {
    return WireframeButton.componentStyles();
  }

  minDimension({ resizingWidth }: { resizingWidth: boolean }) {
    if (resizingWidth) {
      const placeholder = this.placeholder;
      const hasIcon = placeholder.iconLeft || placeholder.iconRight;
      const padding = this.round
        ? PADDING_HORIZONTAL_ROUND
        : PADDING_HORIZONTAL_SQUARE;
      const fallback = padding * 2 + (hasIcon ? SPACER_3 : 0);
      return this.maxItemDimensions?.width ?? fallback;
    } else {
      // 40 is the default height when you place a button.
      return 40;
    }
  }

  get isLink() {
    return this.componentStyle?.toLowerCase().includes('link');
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      width,
      height,
      fontSize,
      uiState = 'default',
      scaleX,
    } = this.fabricObject;
    const { filled, isLink } = this;
    const fill = this.fabricObject.fill ?? 'white';
    const fillColor = filled ? fill : 'transparent';
    const borderRadius = this.round ? ROUND_RADIUS : SQUARE_RADIUS;
    const opacity = uiState === 'default' ? 1 : 0.4;
    // Avoid the case where changing from link to button can lead to a solid
    // gray button, if the user has not set text yet
    const defaultTextColor =
      this.fabricObject.textColor ??
      (isLink ? WIREFRAME_PRIMARY_COLOR : 'white');
    const textColor = filled || isLink ? defaultTextColor : fill;
    const horizontalPadding = this.round
      ? PADDING_HORIZONTAL_ROUND
      : PADDING_HORIZONTAL_SQUARE;

    let renderedDimensions: DimensionStruct;

    const props: Partial<FlexColumnProps> = {};

    if (this.fabricObject.userHasExplicitlySetDimensions()) {
      props.width = width;
      props.height = height;
    }

    // Allow component to shrink when children shrink
    this.resetMaxItemDimensions();

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexColumn
            horizontalAlignment='center'
            verticalAlignment='center'
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
            padding={
              isLink
                ? { vertical: PADDING_LINK }
                : { vertical: PADDING_VERTICAL }
            }
            border={
              isLink ? {} : { color: fill, opacity, lineWidth: BORDER_WIDTH }
            }
            borderRadius={borderRadius}
            fill={{ color: fillColor, opacity }}
            {...props}
          >
            <Placeholder
              wireframe={this}
              color={textColor}
              opacity={opacity}
              padding={
                isLink
                  ? { horizontal: PADDING_LINK }
                  : { horizontal: horizontalPadding }
              }
              size={fontSize}
              gap={isLink ? SPACER_2 : SPACER_3}
              setRenderedDimensions={dimensions => {
                this.updateMaxItemDimensions({
                  dimensions,
                  useUnwrappedWidth: true,
                });
              }}
              textBorder={
                isLink ? { bottom: textColor, lineWidth: 1, opacity } : {}
              }
              textPadding={isLink ? { bottom: UNDERLINE_OFFSET } : {}}
            />
          </FlexColumn>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeButton;
